import React, { useCallback, useEffect, useState } from "react";

import { ApplicationForm, ApplicationState, ApplicationActionType, ActionItem, ActionOnItem } from "../../models/SKApplication";

import ActionPopup from "../../components/ActionPopup";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ContainerNavigation from "../../components/ContainerNavigation";
import Loader from "../../components/Loader";
import dayjs from "dayjs";
import { capitalizeFirstLetter, formatAttributeValue, getCountryName, getLanguageName } from "../../utils/stringFunctions";

import { format } from "date-fns";
import { fetchApplication, getApplicationCounts, updateApplicationStatus } from "../../api/application/applicationServices";
import ArtifactModerationDialog from "../contentModeration/ArtifactModerationDialog";
import ArtifactStateDialog, { DialoageContent } from "../artifacts/create/container/components/ArtifactStateDialog";
import APILoader from "../../components/APILoader";
import SKPagination, { PaginationObject } from "../../components/SKPagination";
import { Pagination } from "../../api/enums";
import TableHeader from "../../components/table/TableHeader";
import { useNavigationBar } from "../../context/NavigationContext";
// import { useUpdateList } from "../applicationManagement/UpdateListContext";
import { TopicTypeString } from "../../models/SKArtifact";

type SKApplicationKey = keyof ApplicationForm;
interface ApplicationTableHeader {
  key: SKApplicationKey;
  label: string;
  span: number;
  sortable: boolean;
}

const ListApplications = () => {
  // const [activePopupId, setActivePopupId] = useState<number | null>(null);

  const [listCount, setListCount] = useState<Record<ApplicationActionType, number>>({
    [ApplicationActionType.approved]: 0,
    [ApplicationActionType.rejected]: 0,
    [ApplicationActionType.submitted]: 0,
  });

  let { showChildRouteAsModal } = useNavigationBar();

  // const { setUpdateListFunction } = useUpdateList();

  const [applications, setApplications] = useState<ApplicationForm[] | null>([]);
  const [selectedType, setSelectedType] = useState<ApplicationActionType>(ApplicationActionType.submitted);
  const [loading, setLoading] = useState(false);
  const [actionOnItem, setActionOnItem] = useState<ActionOnItem | null>(null);
  const [dialogContent, setDialogContent] = useState<DialoageContent | null>(null);
  const moderationDialogActions = [ActionItem.reject, ActionItem.approve];
  const location = useLocation();

  const [paginationObject, setPaginationObject] = useState<PaginationObject | null>(null);
  const [firstDocumentId, setFirstDocumentId] = useState<string | undefined>(undefined);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | undefined>(undefined);

  const headers: ApplicationTableHeader[] = [
    // { key: "action", label: "Action", span: 1, sortable: false }, // Non-sortable column
    { key: "fullName", label: "Name", span: 1, sortable: false },
    { key: "email", label: "Last Report", span: 1, sortable: false },
    { key: "updatedDate", label: "Last Activity", span: 1, sortable: false },
    { key: "channelName", label: "Channel Name", span: 1, sortable: false }, // Non-sortable column
    { key: "applicantType", label: "Type", span: 1, sortable: false },
    { key: "country", label: "Country", span: 1, sortable: false },
    { key: "status", label: "Status", span: 1, sortable: false }, // Non-sortable column
  ];

  const getStatusString = (status: ApplicationState) => {
    if (status === ApplicationState.APPROVED) {
      return "Approved";
    } else if (status === ApplicationState.REJECTED) {
      return "Rejected";
    } else if (status === ApplicationState.DRAFT) {
      return "Draft";
    } else if (status === ApplicationState.SUBMITTED) {
      return "In Review";
    }
  };

  const getFormattedValue = (data: ApplicationForm, key: SKApplicationKey) => {
    switch (key) {
      case "createdDate":
      case "updatedDate":
        return data[key] ? formatAttributeValue(data[key]) : "—";
      case "status":
        return getStatusString(data.status);
      case "country":
        return data.country ? getCountryName(data.country) : "—";
      case "socialLinks":
        return [data.socialLinks.map(item => item.value)];
      default:
        return data[key];
    }
  };

  useEffect(() => {
    showChildRouteAsModal(true);
    return () => {
      showChildRouteAsModal(false);
    };
  }, []);

  /*
   <div className="col-span-1 text-lg font-bold md:text-[11px]">Action</div>
                <div className="col-span-1 text-lg font-bold md:text-[11px]">Name</div>
                {selectedType === ApplicationActionType.Flagged ? <div className="col-span-1 text-lg font-bold md:text-[11px]">Reason</div> : null}
                <div className="col-span-1 text-lg font-bold md:text-[11px]">Category</div>
                <div className="col-span-1 text-lg font-bold md:text-[11px]">Submitted</div>
                <div className="col-span-1 text-lg font-bold md:text-[11px]">Topic</div>
                <div className="col-span-1 text-lg font-bold md:text-[11px]">Age Rating</div>
                <div className="col-span-1 text-lg font-bold md:text-[11px]">Tier</div>
                <div className="col-span-1 text-lg font-bold md:text-[11px]">Primary Language</div>
                <div className="col-span-1 text-lg font-bold md:text-[11px]">Secondary Language</div>
                <div className="col-span-1 text-lg font-bold md:text-[11px]">Region</div>
                <div className="col-span-1 text-lg font-bold md:text-[11px]">Status</div>
  */
  const navigate = useNavigate();
  // useEffect(() => {
  //   fetchData();
  // }, []);

  const fetchCount = async () => {
    let counts = await getApplicationCounts();
    setListCount(counts);
  };

  useEffect(() => {
    fetchCount();
  }, []);

  const fetchData = async (selectedType: ApplicationActionType, moveTo: Pagination) => {
    try {
      let operator = "==";
      let pageSize = 10;
      //let value = selectedType === ApplicationActionType.Published ? State.published : [State.draft, State.review, State.approved, ""];
      setLoading(true);
      setApplications(null);

      let pagination: PaginationObject = {
        firstDoc: undefined,
        lastDoc: undefined,
        hasNext: false,
        hasPrev: false,
      };
      let currentDoc = undefined;

      switch (moveTo) {
        case Pagination.First:
          currentDoc = undefined;
          break;
        case Pagination.Previous:
          currentDoc = paginationObject?.firstDoc;
          break;
        case Pagination.Last:
          currentDoc = paginationObject?.lastDoc;
          switch (selectedType) {
            case ApplicationActionType.approved:
              pageSize = listCount.Approved % pageSize;
              break;
            case ApplicationActionType.submitted:
              pageSize = listCount.Submitted % pageSize;
              break;
            case ApplicationActionType.rejected:
              pageSize = listCount.Rejected % pageSize;
              break;
          }

          break;
        case Pagination.Next:
          currentDoc = paginationObject?.lastDoc;
          break;
      }

      switch (selectedType) {
        case ApplicationActionType.submitted:
          const { docs: reviewedDocs, ...reviewRest } = await fetchApplication(
            ["status"],
            operator,
            [ApplicationState.SUBMITTED],
            pageSize,
            currentDoc,
            moveTo,
            sortConfig
          );
          pagination = reviewRest;
          setApplications(reviewedDocs);
          break;
        case ApplicationActionType.approved:
          const { docs: editReviewedDocs, ...editRevewRest } = await fetchApplication(
            ["status"],
            operator,
            [ApplicationState.APPROVED],
            pageSize,
            currentDoc,
            moveTo,
            sortConfig
          );
          pagination = editRevewRest;
          setApplications(editReviewedDocs);
          break;
        case ApplicationActionType.rejected:
          const { docs: rejectedDocs, ...rejectedRest } = await fetchApplication(
            ["status"],
            operator,
            [ApplicationState.REJECTED],
            pageSize,
            currentDoc,
            moveTo,
            sortConfig
          );
          pagination = rejectedRest;
          setApplications(rejectedDocs);
          break;
        default:
          break;
      }

      if (moveTo === Pagination.First || (pagination.firstDoc && pagination.firstDoc.id === firstDocumentId)) {
        setFirstDocumentId(pagination.firstDoc?.id);
        pagination.hasPrev = false; // Disable previous if we are on the first page
      } else if (moveTo === Pagination.Previous && pagination.firstDoc && pagination.firstDoc.id === firstDocumentId) {
        pagination.hasPrev = false; // Disable previous if we are back on the first page
      }
      setPaginationObject(pagination);
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  // const updateApplicationList = useCallback(
  //   (applicationId: string, actionItem: ActionItem) => {
  //     // Find the application that matches the applicationId

  //     const item = applications?.find(application => application.id === applicationId);

  //     if (item) {
  //       // Use the ActionItem to determine the new state and substate
  //       const { state: newState, substate: newSubstate } = getStatesForAction(actionItem)[0] || [];
  //       const oldState = item.status;
  //       const oldSubstate = item.subStatus;

  //       // Helper function to map state/substate to ApplicationActionType
  //       const getActionTypeForState = (state: ApplicationState, substate: ApplicationSubstate): ApplicationActionType | null => {
  //         if (state === ApplicationState.IN_REVIEW) return ApplicationActionType.review;
  //         if (state === ApplicationState.ACTIVE) {
  //           if (substate === ApplicationSubstate.EDIT_IN_REVIEW) return ApplicationActionType.edit_review;
  //           return ApplicationActionType.active;
  //         }
  //         if (state === ApplicationState.DRAFT) {
  //           if (substate === ApplicationSubstate.REJECTED) return ApplicationActionType.rejected;
  //           if (substate === ApplicationSubstate.WITHDRAWN) return ApplicationActionType.withdrawn;
  //         }
  //         return null;
  //       };

  //       // Identify previous and new action types
  //       const previousType = getActionTypeForState(oldState, oldSubstate);
  //       const newType = getActionTypeForState(newState, newSubstate);

  //       // Update list count based on the action
  //       setListCount(prev => {
  //         const updatedCounts = { ...prev };

  //         // Decrement the count for the old action type
  //         if (previousType && updatedCounts[previousType] > 0) {
  //           updatedCounts[previousType] -= 1;
  //         }

  //         // Increment the count for the new action type
  //         if (newType && !(oldState === ApplicationState.ACTIVE && newState === ApplicationState.ACTIVE)) {
  //           updatedCounts[newType] = (updatedCounts[newType] || 0) + 1;
  //         }

  //         return updatedCounts;
  //       });

  //       // Update the applications state
  //       setApplications(prev => {
  //         if (!prev) return null;

  //         return prev.map(existingApplication =>
  //           existingApplication.id === applicationId ? { ...existingApplication, state: newState, substate: newSubstate } : existingApplication
  //         );
  //       });
  //     }

  //     // Optionally refresh the data for the selected type
  //     fetchData(selectedType, Pagination.First);
  //   },
  //   [applications, selectedType, setApplications, setListCount]
  // );

  // useEffect(() => {
  //   setUpdateListFunction(() => updateApplicationList);
  // }, [updateApplicationList, setUpdateListFunction]);

  useEffect(() => {
    fetchData(selectedType, Pagination.First);
  }, [selectedType]);

  useEffect(() => {
    setPaginationObject(null);
  }, [selectedType]);

  function getModerationDialogContent(action: ActionItem): DialoageContent | null {
    switch (action) {
      case ActionItem.approve:
        return {
          message: "Are you sure you will like to Approve Application?",
          description: "This will allow the applicant to continue with channel onboarding.",
          actionTitle: ActionItem.approve,
          destructiv: false,
        };
      case ActionItem.reject:
        return {
          message: "Are you sure you will like to Reject Application?",
          description: "This will stop the applicant from continuing with channel onboarding.",
          actionTitle: ActionItem.reject,
          destructiv: true,
        };

      default:
        return null;
    }
  }

  // const getPopItemsForId = (id: string): ActionItem[] => {
  //   let content = applications?.find(element => element.id === id);
  //   if (content) {
  //     let mixedItem = { state: content.status, subState: content.subStatus };
  //     return getAdminActionableItems(mixedItem);
  //   }
  //   return [];
  // };

  // function getActionableItems(content: SKApplication): ActionItem[] {
  //   const actions: { [key in ApplicationState]: ActionItem[] } = {
  //     draft: [],
  //     review: [ActionItem.approve, ActionItem.do_not_approve, ActionItem.block],
  //     published: [ActionItem.make_inactive, ActionItem.block],
  //     approved: [],
  //     pre_approved: [],
  //     rejected: [ActionItem.active, ActionItem.delete],
  //     need_more_info: [],
  //     blocked: [ActionItem.remove_block, ActionItem.delete],
  //     suspended: [ActionItem.remove_suspension, ActionItem.delete],
  //   };

  //   return actions[content.status] || [];
  // }

  // const updateStatus = async (contentId: string, actionItem: ActionItem, reason?: string, note?: string) => {
  //   const content = artifacts?.find(item => item.id === contentId);
  //   switch (actionItem) {
  //     case ActionItem.approve:
  //       await updateApplicationStatus(contentId, content?.autoPublish ? State.published : State.approved, reason, note);
  //       // await updateArtifact(contentId, { status: content?.autoPublish ? State.published : State.approved });
  //       updateLocalStatus(contentId, content?.autoPublish ? State.published : State.approved);
  //       break;
  //     case ActionItem.do_not_approve:
  //       //await updateArtifact(contentId, { status: State.rejected });
  //       await updateApplicationStatus(contentId, State.rejected, reason, note);
  //       updateLocalStatus(contentId, State.rejected);
  //       break;
  //     case ActionItem.keep_published:
  //       await updateApplication(contentId, { flaged: false });
  //       removeFlaggedItemLocally(contentId);
  //       // updateLocalStatus(contentId, State.published);
  //       break;
  //     case ActionItem.unpublish:
  //       // await updateArtifact(contentId, { status: State.draft });
  //       await updateApplicationStatus(contentId, State.draft, reason, note);
  //       updateLocalStatus(contentId, State.draft);
  //       break;
  //     case ActionItem.publish:
  //       // await updateArtifact(contentId, { status: State.published });
  //       await updateApplicationStatus(contentId, State.published, reason, note);
  //       updateLocalStatus(contentId, State.published);
  //       break;
  //     case ActionItem.block:
  //       // await updateArtifact(contentId, { status: State.blocked });
  //       await updateApplicationStatus(contentId, State.blocked, reason, note);
  //       updateLocalStatus(contentId, State.blocked);
  //       break;
  //   }
  // };

  const removeFlaggedItemLocally = (contentId: string) => {
    let items = applications!.filter(item => item.id !== contentId);
    setApplications(items);
  };

  // const updateStatus = async (
  //   contentId: string,
  //   actionItem: ActionItem,
  //   state: ApplicationState,
  //   substate: ApplicationSubstate,
  //   reason?: string,
  //   note?: string
  // ) => {
  //   switch (actionItem) {
  //     case ActionItem.approve:
  //       if (state === ApplicationState.IN_REVIEW && substate === ApplicationSubstate.NONE) {
  //         await updateApplicationStatus(contentId, ApplicationState.ACTIVE, ApplicationSubstate.NONE, actionItem, reason, note);
  //         updateLocalStatus(contentId, ApplicationState.ACTIVE, ApplicationSubstate.NONE);
  //       }
  //       break;

  //     case ActionItem.do_not_approve:
  //       if (state === ApplicationState.IN_REVIEW && substate === ApplicationSubstate.NONE) {
  //         await updateApplicationStatus(contentId, ApplicationState.DRAFT, ApplicationSubstate.REJECTED, actionItem, reason, note);
  //         updateLocalStatus(contentId, ApplicationState.DRAFT, ApplicationSubstate.REJECTED);
  //       }
  //       break;

  //     case ActionItem.keep_published:
  //       if (state === ApplicationState.ACTIVE) {
  //         await updateApplication(contentId, { flagged: false });
  //         await updateApplicationHistory(contentId, actionItem);
  //         removeFlaggedItemLocally(contentId);
  //       }
  //       break;

  //     case ActionItem.make_inactive:
  //       if (state === ApplicationState.ACTIVE) {
  //         await updateApplicationStatus(contentId, ApplicationState.DRAFT, ApplicationSubstate.NONE, actionItem, reason, note);
  //         updateLocalStatus(contentId, ApplicationState.DRAFT, ApplicationSubstate.NONE);
  //       }
  //       break;

  //     case ActionItem.publish:
  //       if (state === ApplicationState.ACTIVE && substate === ApplicationSubstate.EDIT_IN_REVIEW) {
  //         await updateApplicationStatus(contentId, ApplicationState.ACTIVE, ApplicationSubstate.NONE, actionItem, reason, note);
  //         updateLocalStatus(contentId, ApplicationState.ACTIVE, ApplicationSubstate.NONE);
  //       }
  //       break;

  //     case ActionItem.suspend:
  //       if (state === ApplicationState.ACTIVE) {
  //         await updateApplicationStatus(contentId, ApplicationState.DRAFT, ApplicationSubstate.REJECTED, actionItem, reason, note);
  //         updateLocalStatus(contentId, ApplicationState.DRAFT, ApplicationSubstate.REJECTED);
  //       }
  //       break;

  //     case ActionItem.remove_suspension:
  //       if (state === ApplicationState.DRAFT && substate === ApplicationSubstate.REJECTED) {
  //         await updateApplicationStatus(contentId, ApplicationState.ACTIVE, ApplicationSubstate.NONE, actionItem, reason, note);
  //         updateLocalStatus(contentId, ApplicationState.ACTIVE, ApplicationSubstate.NONE);
  //       }
  //       break;

  //     case ActionItem.block:
  //       if (state === ApplicationState.ACTIVE) {
  //         await updateApplicationStatus(contentId, ApplicationState.DRAFT, ApplicationSubstate.WITHDRAWN, actionItem, reason, note);
  //         updateLocalStatus(contentId, ApplicationState.DRAFT, ApplicationSubstate.WITHDRAWN);
  //       }
  //       break;

  //     case ActionItem.remove_block:
  //       if (state === ApplicationState.DRAFT && substate === ApplicationSubstate.WITHDRAWN) {
  //         await updateApplicationStatus(contentId, ApplicationState.ACTIVE, ApplicationSubstate.NONE, actionItem, reason, note);
  //         updateLocalStatus(contentId, ApplicationState.ACTIVE, ApplicationSubstate.NONE);
  //       }
  //       break;

  //     default:
  //       throw new Error(`Invalid action item: ${actionItem} for state: ${state} and substate: ${substate}`);
  //   }
  // };
  const updateStatus = async (contentId: string, actionItem: ActionItem) => {
    switch (actionItem) {
      case ActionItem.approve:
        await updateApplicationStatus(contentId, ApplicationState.APPROVED);
        updateLocalStatus(contentId, ApplicationState.APPROVED);
        break;
      case ActionItem.reject:
        await updateApplicationStatus(contentId, ApplicationState.DRAFT);
        updateLocalStatus(contentId, ApplicationState.REJECTED);
        break;
      default:
    }
  };

  useEffect(() => {
    if (actionOnItem?.action) {
      const contentDialog = getModerationDialogContent(actionOnItem.action);
      setDialogContent(contentDialog);
    }
  }, [actionOnItem]);

  const updateLocalStatus = (contentId: string, state: ApplicationState) => {
    let items = applications!.filter(item => {
      if (item.id === contentId) {
        var neItem = item;
        neItem.status = state;
        return neItem;
      } else {
        return item;
      }
    });

    setApplications(items);
  };

  const deleteApplicationLocally = (contentId: string) => {
    let items = applications!.filter(item => item.id !== contentId);
    setApplications(items);
  };

  const showApplication = async (applicationId: string) => {
    navigate("../applicationDetail", { state: { applicationId, backgroundLocation: location } });
  };
  // .w-button {
  //   @apply inline-block bg-[#3898EC] text-[white] leading-[inherit] no-underline cursor-pointer px-[15px] py-[9px] rounded-none border-0;
  // }

  useEffect(() => {
    fetchData(selectedType, Pagination.First);
  }, [sortConfig]);

  const handleSortChange = (config: { key: string; direction: "asc" | "desc" } | undefined) => {
    console.log("Sort Config:", config);
    setSortConfig(config);
  };

  const totalSpan = headers.reduce((sum, item) => sum + item.span, 0);
  const gridColsClass = `grid-cols-${totalSpan}`; //`grid-cols-[repeat(${totalSpan},_1fr)_auto]`;

  return (
    <div className="flex-1 bg-white">
      <div className="flex flex-col flex-1">
        {actionOnItem?.action && dialogContent ? (
          <ArtifactStateDialog
            action={actionOnItem?.action}
            onClose={() => setActionOnItem(null)}
            onUpdate={async () => {
              const action = actionOnItem?.action;
              const contentId = actionOnItem?.itemid;
              await updateStatus(contentId, action);
              setActionOnItem(null);
            }}
            dialogeContent={dialogContent}
          />
        ) : (
          <></>
        )}

        <ContainerNavigation
          backArrow={false}
          title={"Application Inbox"}
          buttonTitle={"Create"}
          subtitle={"All Applications"}
          enableLink={true}
          onSearch={() => {}}
        />

        <div className="flex flex-row gap-4 px-2  mt-[30px] overflow-x-auto">
          <Link
            to={"#"}
            onClick={e => {
              e.preventDefault();
              setSelectedType(ApplicationActionType.submitted);
            }}
            className={
              (selectedType === ApplicationActionType.submitted ? " border-black " : " border-white ") +
              "border-b-2 text-lg font-bold font flex items-center dark:text-black text-nowrap"
            }
          >
            In Review . {listCount[ApplicationActionType.submitted]}
          </Link>
          <Link
            to={"#"}
            onClick={e => {
              e.preventDefault();
              setSelectedType(ApplicationActionType.approved);
            }}
            className={
              (selectedType === ApplicationActionType.approved ? " border-black " : " border-white ") +
              "border-b-2 text-lg font-bold font flex items-center dark:text-black text-nowrap"
            }
          >
            Approved . {listCount[ApplicationActionType.approved]}
          </Link>

          <Link
            to={"#"}
            onClick={e => {
              e.preventDefault();
              setSelectedType(ApplicationActionType.rejected);
            }}
            className={
              (selectedType === ApplicationActionType.rejected ? " border-black " : " border-white ") +
              "border-b-2 text-lg font-bold flex items-center mx-2 dark:text-black text-nowrap"
            }
          >
            Rejected . {listCount[ApplicationActionType.rejected]}
          </Link>
        </div>

        <div className="flex flex-col p-2 overflow-y-auto h-[calc(100vh-354px)]">
          {/* <div key="header" className="hidden bg-white rounded md:block ">
              <div className="grid grid-cols-12 gap-4 w-[1200px] border-b-[1px] border-gray-300 pb-3">
                <div className="col-span-1 text-lg font-bold md:text-[11px]">Action</div>
                <div className="col-span-1 text-lg font-bold md:text-[11px]">Name</div>
                {selectedType === ApplicationActionType.Flagged ? <div className="col-span-1 text-lg font-bold md:text-[11px]">Reason</div> : null}
                <div className="col-span-1 text-lg font-bold md:text-[11px]">Category</div>
                <div className="col-span-1 text-lg font-bold md:text-[11px]">Submitted</div>
                <div className="col-span-1 text-lg font-bold md:text-[11px]">Topic</div>
                <div className="col-span-1 text-lg font-bold md:text-[11px]">Age Rating</div>
                <div className="col-span-1 text-lg font-bold md:text-[11px]">Tier</div>
                <div className="col-span-1 text-lg font-bold md:text-[11px]">Primary Language</div>
                <div className="col-span-1 text-lg font-bold md:text-[11px]">Secondary Language</div>
                <div className="col-span-1 text-lg font-bold md:text-[11px]">Region</div>
                <div className="col-span-1 text-lg font-bold md:text-[11px]">Status</div>
              </div>
            </div> */}
          <TableHeader headers={headers} onSortChange={handleSortChange} className={`w-full ${gridColsClass}`} />
          {/* id?: string;
    name?: string;
    description?: string;
    profileImageUrl?: string;
    backgroundImageUrl?: string;
    language?: string;
    secondLanguage?: string;
    country?: string;
    externalLinks: ExternalLink[] | null;
    topic: TopicType | null;
    age?: number;
    status: State;
    certifyFirst?: boolean;
    certifySecond?: boolean;
    tier: Tier;
    autoPublish?: boolean;
    createdDate?: Timestamp; */}

          {/* {loading ? <Loader /> : <></>} */}
          <div className="overflow-y-scroll ">
            <APILoader loading={loading} />

            {applications?.map(content => {
              return (
                <div
                  key={content.id}
                  className="pt-2 my-2 group hover:cursor-pointer hover:font-semibold hover:bg-skseparatorgray md:min-w-[980px]"
                  onClick={() => navigate(`${content.id}`)}
                >
                  {/* Table view for browser  */}
                  <div className="hidden md:block">
                    <div
                      key={content.id}
                      className={`grid items-center ${gridColsClass} gap-4 w-full justify-center  border-b-[1px] border-gray-300 pb-3`}
                    >
                      {headers.map(header => (
                        <div key={header.key} className={`col-span-${header.span} text-sm font-bold capitalize md:text-xs md:font-normal`}>
                          {getFormattedValue(content, header.key)}
                        </div>
                      ))}
                      {/* <div className="text-sm md:text-[14px] md:col-span-2 md:font-normal ">{content.name ?? ""}</div>

                        <div className="col-span-1 text-sm md:text-[14px] capitalize">{content.applicationType}</div>
                        <div className="col-span-1 text-sm md:text-[14px]">
                          {" "}
                          {content.createdDate ? format(content.createdDate.toDate(), "MMM, dd yyyy") : "--"}
                        </div>
                        <div className="col-span-1 text-sm md:text-[14px]">{content.topic ? TopicTypeString[content.topic] : ""}</div>

                        <div className="col-span-1 text-lg font-bold md:font-normal md:text-[14px]">
                          {content.language ? getLanguageName(content.language) : ""}
                        </div>

                        <div className="col-span-1 text-lg font-bold md:font-normal md:text-[14px]">
                          {content.country ? getCountryName(content.country) : "United States"}
                        </div>
                        <div className="col-span-1 text-sm md:text-[14px]">{content.status}</div> */}
                    </div>
                  </div>

                  {/* Card view for mobile  */}
                  <div className="block md:hidden">
                    <div className="relative flex flex-col p-4 mb-4 bg-white rounded-xl border-[1px] border-skseparatorgray">
                      <div className="flex flex-row items-center justify-between">
                        <div className="flex flex-col">
                          <p className="text-gray-500">Name</p>
                          <Link
                            // to={`/applicationDetail/${content.id}`}
                            to={`${content.id}`}
                            className="text-base capitalize hover:cursor-pointer"
                            state={{ backgroundLocation: location }}
                            // onClick={() => (content.id ? showApplication(content.id) : {})}
                          >
                            {content.fullName ?? ""}
                          </Link>
                        </div>
                        {/* <ActionPopup
                          getValues={() => getPopItemsForId(content.id!)}
                          onSelect={actionItem => {
                            switch (actionItem) {
                              case ActionItem.pre_approve:
                                setOptions(options_preApproval);
                                break;
                              case ActionItem.suspend:
                              case ActionItem.block:
                                setOptions(options_block);
                                break;
                              default:
                                setOptions(options_normal);
                                break;
                            }
                            setActionOnItem({
                              itemid: content.id!,
                              action: actionItem,
                            });
                          }}
                        /> */}
                      </div>
                      <div className="flex flex-row mt-2">
                        <div className="flex flex-col flex-1">
                          <div className="text-gray-500">Type</div>
                          <div className="text-sm capitalize">{content.applicantType!}</div>
                        </div>

                        <div className="flex flex-col flex-1">
                          <div className="text-gray-500">Country</div>
                          <div className="text-sm capitalize">{content.country}</div>
                        </div>
                      </div>
                      <div className="flex flex-row mt-2 ">
                        <div className="flex flex-col flex-1">
                          <div className="text-gray-500">Activity Date</div>
                          <div className="text-sm">{content.updatedDate ? format(content.updatedDate.toDate(), "MMM, dd yyyy") : "--"}</div>
                        </div>
                        <div className="flex flex-col flex-1">
                          <div className="text-gray-500">Status</div>
                          <div className="text-sm capitalize">{content.status}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {(paginationObject?.hasNext || paginationObject?.hasPrev) && (
          <div className="items-center content-center mx-auto ">
            <SKPagination
              onFirst={() => {
                fetchData(selectedType, Pagination.First);
              }}
              onLast={() => {
                fetchData(selectedType, Pagination.Last);
              }}
              onNext={() => {
                fetchData(selectedType, Pagination.Next);
              }}
              onPrev={() => {
                fetchData(selectedType, Pagination.Previous);
              }}
              hasNext={paginationObject.hasNext || false}
              hasPrev={paginationObject.hasPrev || false}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ListApplications;
